import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const partnerBrands = [
  {
    name: "Hytera",
    category: "Body-Worn Video & Communications",
    tagline: "Malaysia's primary BWV solution for law enforcement",
    logo: "https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/6d2ef031-b05d-445b-add7-cb1ee7ec1599_Hytera-Logo-RGB.png?v=2b451d70b2f39a7a3313fb4cfd9d1d6a",
    href: "/hytera-bwv",
    ctaLabel: "BWC Spotlight",
    invert: false,
    accentBg: "bg-[#f5f3ee]",
  },
  {
    name: "Arcfra",
    category: "HCI & Storage Infrastructure",
    tagline: "VMware & Nutanix alternative — 60% lower TCO",
    logo: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/e4390374f33642fbb291c106f3ac5eaa.jpeg",
    href: "/arcfra-spotlight",
    ctaLabel: "HCI Spotlight",
    invert: false,
    accentBg: "bg-[#1a1a1a]",
  },
  {
    name: "ExaGrid",
    category: "Enterprise Backup Storage",
    tagline: "Fastest recovery, ransomware-proof, scale-out",
    logo: "https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/332e9681-9df1-4dcd-93f2-cf1098c6260f_exagrid-logo.png?v=ddd934934df6f2e00eabba5539238300",
    href: "/exagrid-spotlight",
    ctaLabel: "Backup Spotlight",
    invert: false,
    accentBg: "bg-[#f5f3ee]",
  },
  {
    name: "Honeywell",
    category: "Rugged Mobile Computers",
    tagline: "MIL-SPEC handhelds for warehouse & field ops",
    logo: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/6cb0550feabd77beb61e8450691b1813.png",
    href: "/rugged-spotlight",
    ctaLabel: "Rugged Spotlight",
    invert: false,
    accentBg: "bg-[#f5f3ee]",
  },
  {
    name: "Dell Rugged Pro",
    category: "Rugged Laptops & Tablets",
    tagline: "MIL-STD-810H laptops for field engineers",
    logo: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/b7a6eed8a3607d563d28271b61809705.png",
    href: "/rugged-spotlight",
    ctaLabel: "Rugged Spotlight",
    invert: false,
    accentBg: "bg-[#f5f3ee]",
  },
  {
    name: "Synology",
    category: "NAS & Cloud Storage",
    tagline: "Enterprise NAS and cloud backup management",
    logo: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/f3fe09ee56f23a05de110d3c308e3d60.png",
    href: "/brands",
    ctaLabel: "Brand Profile",
    invert: false,
    accentBg: "bg-[#f5f3ee]",
  },
];

export default function PartnerBrandsSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal({ threshold: 0.05 });
  const { ref: trustRef, isVisible: trustVisible } = useScrollReveal();

  return (
    <section className="py-20 lg:py-28 bg-[#f9f7f4]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">
              Our Ecosystem
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight">
              Authorized Distributor &amp; <br />
              <strong>Technology Partners</strong>
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-md leading-relaxed">
              SE Distribution is the <strong className="text-[#1a1a1a]">authorized distributor</strong> for Hytera, Arcfra &amp; ExaGrid — and a <strong className="text-[#1a1a1a]">technology partner</strong> for Synology, Honeywell &amp; Dell Rugged Pro.
            </p>
          </div>
          <Link
            to="/brands"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1a1a1a] border border-[#1a1a1a] px-5 py-2.5 rounded-md hover:bg-[#1a1a1a] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
          >
            View All Technology Partners
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Brand Grid */}
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {partnerBrands.map((brand, i) => (
            <Link
              key={brand.name}
              to={brand.href}
              className={`group bg-white rounded-xl border border-gray-100 p-6 hover:border-[#1a1a1a]/20 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer flex flex-col ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{
                transitionProperty: "opacity, transform, border-color",
                transitionDuration: gridVisible ? "600ms" : "200ms",
                transitionTimingFunction: "ease-out",
                transitionDelay: gridVisible ? `${i * 60}ms` : "0ms",
              }}
            >
              {/* Logo Area */}
              <div className="h-12 flex items-center mb-5">
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="max-h-9 w-auto max-w-[130px] object-contain"
                  style={{ filter: brand.invert ? "brightness(0) invert(1) sepia(0) saturate(0)" : "none" }}
                />
              </div>

              {/* Content */}
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{brand.category}</p>
                <h3 className="font-bold text-[#1a1a1a] text-sm mb-2">{brand.name}</h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-4">{brand.tagline}</p>
              </div>

              {/* CTA */}
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#1a1a1a] group-hover:gap-2.5 transition-all mt-auto pt-4 border-t border-gray-100">
                <span>{brand.ctaLabel}</span>
                <i className="ri-arrow-right-line text-xs"></i>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom Trust Bar */}
        <div
          ref={trustRef}
          className={`mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 text-center sm:text-left transition-all duration-700 ease-out ${trustVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg flex-shrink-0">
              <i className="ri-verified-badge-line text-[#1a1a1a] text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1a1a1a]">100% Genuine Products</p>
              <p className="text-xs text-gray-400">All products carry full manufacturer warranty</p>
            </div>
          </div>
          <div className="hidden sm:block w-px h-10 bg-gray-200"></div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg flex-shrink-0">
              <i className="ri-customer-service-2-line text-[#1a1a1a] text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1a1a1a]">Local Support Team</p>
              <p className="text-xs text-gray-400">Based in Kuala Lumpur, Mon–Fri 9:30–6:30</p>
            </div>
          </div>
          <div className="hidden sm:block w-px h-10 bg-gray-200"></div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg flex-shrink-0">
              <i className="ri-award-line text-[#1a1a1a] text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1a1a1a]">3 Authorized Distributorships</p>
              <p className="text-xs text-gray-400">Hytera, Arcfra &amp; ExaGrid — official distributor for Malaysia</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
